import { useEffect, useState } from 'react';
import {
  X,
  Download,
  Share2,
  Shield,
  FileText,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { ShareOptionsModal } from '../shared/ShareOptionsModal';

interface DocumentViewerModalProps {
  onClose: () => void;
  documentHash: string;
  documentTitle: string;
  /** @deprecated Use documentTitle instead */
  vaccineName?: string;
}

export function DocumentViewerModal({
  onClose,
  documentHash,
  documentTitle,
  vaccineName,
}: DocumentViewerModalProps) {
  const title = documentTitle || vaccineName || 'Document';
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('');
  const [contentType, setContentType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareOptions, setShowShareOptions] = useState(false);

  useEffect(() => {
    let mounted = true;
    let blobUrl: string | null = null;

    const fetchPreview = async () => {
      try {
        // If documentHash is already a full URL (e.g. from some insurance flows), use it directly
        if (
          documentHash.startsWith('http://') ||
          documentHash.startsWith('https://')
        ) {
          if (mounted) {
            setPreviewUrl(documentHash);
            setFilename(documentHash.split('/').pop() || 'document');
            setContentType('application/pdf');
          }
          return;
        }
        const encodedKey = encodeURIComponent(documentHash);
        const res = await apiClient.get(
          `/documents/${encodedKey}/download`,
          { params: { disposition: 'inline' }, responseType: 'blob' }
        );

        if (!mounted) return;

        const blob = res.data as Blob;
        const ct =
          res.headers['content-type'] ||
          (blob.type !== 'application/octet-stream' ? blob.type : '') ||
          'application/pdf';

        const disposition =
          (res.headers['content-disposition'] as string) || '';
        const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
        const extractedName = filenameMatch?.[1] || 'document';

        blobUrl = URL.createObjectURL(blob);
        setPreviewUrl(blobUrl);
        setFilename(extractedName);
        setContentType(ct);
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error ? err.message : 'Failed to load document'
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchPreview();
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [documentHash]);

  const handleDownload = async () => {
    try {
      if (
        documentHash.startsWith('http://') ||
        documentHash.startsWith('https://')
      ) {
        const a = document.createElement('a');
        a.href = documentHash;
        a.download = filename || 'document';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      const encodedKey = encodeURIComponent(documentHash);
      const res = await apiClient.get(
        `/documents/${encodedKey}/download`,
        { params: { disposition: 'attachment' }, responseType: 'blob' }
      );
      const blob = res.data as Blob;
      const disposition =
        (res.headers['content-disposition'] as string) || '';
      const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
      const downloadName = filenameMatch?.[1] || filename || 'document';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  };

  const isImage = contentType.startsWith('image/');
  const isPdf =
    contentType === 'application/pdf' ||
    previewUrl?.toLowerCase().includes('.pdf');

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col border dark:border-white/8">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-white/8 flex-shrink-0">
          <div>
            <h3
              className="text-xl text-gray-900 dark:text-white mb-1"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-white/50 hover:text-gray-600 dark:hover:text-white/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Document Preview Area */}
        <div className="flex-1 min-h-0 overflow-auto p-6 bg-gray-50 dark:bg-[#0F0E16]">
          <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg min-h-[400px] flex items-center justify-center">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin" />
                <span
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Loading document...
                </span>
              </div>
            ) : error ? (
              <div className="text-center py-12 px-6">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h4
                  className="text-lg mb-2 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  Unable to Load Document
                </h4>
                <p
                  className="text-sm text-gray-600 dark:text-white/70 max-w-md mx-auto mb-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {error}
                </p>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  <Download className="w-4 h-4" />
                  Try Download
                </button>
              </div>
            ) : previewUrl ? (
              <div className="w-full h-full min-h-[400px] p-4">
                {isImage ? (
                  <img
                    src={previewUrl}
                    alt={`${title} document`}
                    className="max-w-full max-h-[75vh] object-contain mx-auto rounded"
                  />
                ) : isPdf ? (
                  <iframe
                    src={previewUrl}
                    title={`${title} document`}
                    className="w-full h-[75vh] min-h-[500px] border-0 rounded"
                  />
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-24 h-24 text-gray-300 dark:text-white/20 mx-auto mb-4" />
                    <p
                      className="text-sm text-gray-600 dark:text-white/70 mb-4"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Preview not available for this file type.
                    </p>
                    <button
                      onClick={handleDownload}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Actions Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-white/8 bg-white dark:bg-[#16151D]">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span
              className="text-xs text-gray-500 dark:text-white/50"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Document stored securely • {filename || 'certificate'}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowShareOptions(true)}
              disabled={!previewUrl && !documentHash}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90 disabled:opacity-50"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <button
              onClick={() => previewUrl && window.open(previewUrl, '_blank')}
              disabled={!previewUrl}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90 disabled:opacity-50"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <FileText className="w-4 h-4" />
              Open
            </button>
            <button
              onClick={handleDownload}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
        </div>
      </div>
      <ShareOptionsModal
        isOpen={showShareOptions}
        onClose={() => setShowShareOptions(false)}
        title={`${title} document`}
        message={`Sharing document: ${title}`}
        url={previewUrl || window.location.href}
        fileName={filename || title}
        shareType="document"
      />
    </div>
  );
}
